<script setup lang="ts">
import type { BlockList } from '@/models'
import { type PropType, ref } from 'vue'
import FlexibleBlock from '../../blockStyles/FlexibleBlock.vue'
import { StrapiBlocks } from 'vue-strapi-blocks-renderer'
import { mainBackgroundClasses } from '@/constants'

defineProps({
  blockList: {
    type: Object as PropType<BlockList>,
    required: true,
  },
  isHeaderBlock: {
    type: Boolean,
    default: false,
  },
})

interface Card {
  bgColor: string
  textColor: string
}

const cardStyling: Card[] = [
  {
    bgColor: 'bg-[#98D3EE]',
    textColor: 'text-[#123A35]',
  },
  {
    bgColor: 'bg-[#227570]',
    textColor: 'text-white',
  },
  {
    bgColor: 'bg-[#DC8BD7]',
    textColor: 'text-[#123A35]',
  },
  {
    bgColor: 'bg-[#DCC866]',
    textColor: 'text-[#123A35]',
  },
]

const activeCard = ref(1)

function selectCard(index: number) {
  activeCard.value = index
}
</script>

<template>
  <div>
    <FlexibleBlock
      :blockData="blockList.details"
      :isHeaderBlock="isHeaderBlock"
      :customClass="mainBackgroundClasses(blockList.details.backgroundColour)"
    />
    <div class="bg-white">
      <div class="relative flex max-xl:flex-col items-end overflow-visible mx-8">
        <div
          v-for="(block, index) in blockList.blocks"
          :key="index"
          @click="selectCard(index)"
          class="relative flex flex-col justify-between rounded-3xl shadow-lg shrink-0 first:ml-0 -ml-8 transition-all font-alexandria box-border"
          :class="[
            cardStyling[index]?.bgColor,
            cardStyling[index]?.textColor,
            activeCard === index
              ? 'z-30 w-[calc(40%+(var(--spacing)*8*3))]' // expanded width
              : 'z-30 w-[20%]', // collapsed width
            'xl:h-120 p-6 max-xl:mb-4 max-xl:w-full',
          ]"
        >
          <div>
            <!-- title -->
            <div v-if="block.title" class="flex flex-row md:mt-8">
              <h1 class="text-5xl w-full text-shadow-lg">
                {{ block.title }}
              </h1>
            </div>

            <!-- subtitle -->
            <div v-if="block.subtitle" class="flex flex-row mt-4">
              <h1 class="text-2xl w-full font-extralight">
                {{ block.subtitle }}
              </h1>
            </div>

            <!-- description -->
            <div
              v-if="block.description && activeCard === index"
              class="flex flex-col xl:mt-24 max-xl:mt-8 mr-8 strapi-block text-2xl font-extralight"
            >
              <StrapiBlocks :content="block.description" />
            </div>
          </div>
          <button
            v-if="activeCard !== index"
            @click.stop="selectCard(index)"
            class="absolute bottom-5 xl:left-5 max-xl:right-5 flex items-center justify-center w-10 h-10 border border-slate-800 rounded-full text-xl text-slate-800 cursor-pointer"
          >
            +
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.cards h1 {
  font-size: 3rem;
}
</style>
